/**
 * Xero Connection Status Component
 * Displays and manages Xero connection status
 */

import React, { useState, useEffect } from 'react';
import { invoiceService } from '../../services/invoiceService';
import './XeroConnectionStatus.css';

const XeroConnectionStatus = ({ onTestConnection, isProcessing }) => {
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    testConnection();
  }, []);

  const testConnection = async () => {
    setIsLoading(true);
    try {
      const response = await invoiceService.testXeroConnection();
      if (response.success) {
        setConnectionStatus({
          status: 'connected',
          message: 'Connection successful',
          timestamp: new Date().toISOString(),
          details: response.data
        });
      } else {
        setConnectionStatus({
          status: 'failed',
          message: response.message || 'Connection failed',
          timestamp: new Date().toISOString(),
          details: response.data
        });
      }
    } catch (error) {
      setConnectionStatus({
        status: 'error',
        message: 'Connection test error',
        timestamp: new Date().toISOString(),
        details: null
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getConnectionStatusClass = (status) => {
    switch (status) {
      case 'connected':
        return 'status-badge connected';
      case 'failed':
        return 'status-badge failed';
      case 'error':
        return 'status-badge error';
      default:
        return 'status-badge unknown';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="xero-connection-status">
        <div className="loading-container">
          <div className="loading-spinner">Testing connection...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="xero-connection-status">
      <div className="connection-section">
        <h3>Connection Status</h3>
        <div className="connection-status">
          <div className="status-info">
            <div className="status-header">
              <span className="status-label">Xero API Connection</span>
              <span className={getConnectionStatusClass(connectionStatus?.status)}>
                {connectionStatus?.status || 'Unknown'}
              </span>
            </div>
            <div className="status-details">
              <span className="status-message">{connectionStatus?.message || 'Not tested'}</span>
              <span className="status-timestamp">
                Last tested: {formatDate(connectionStatus?.timestamp)}
              </span>
            </div>
          </div>
          <div className="status-actions">
            <button
              className="btn btn-primary"
              onClick={testConnection}
              disabled={isProcessing}
            >
              <i className="fas fa-plug"></i>
              Test Connection
            </button>
          </div>
        </div>
      </div>

      {connectionStatus?.details && (
        <div className="connection-details">
          <h4>Connection Details</h4>
          <div className="details-grid">
            {connectionStatus.details.tenant_id && (
              <div className="detail-item">
                <span className="detail-label">Tenant ID:</span>
                <span className="detail-value">{connectionStatus.details.tenant_id}</span>
              </div>
            )}
            {connectionStatus.details.tenant_name && (
              <div className="detail-item">
                <span className="detail-label">Tenant Name:</span>
                <span className="detail-value">{connectionStatus.details.tenant_name}</span>
              </div>
            )}
            {connectionStatus.details.tenant_type && (
              <div className="detail-item">
                <span className="detail-label">Tenant Type:</span>
                <span className="detail-value">{connectionStatus.details.tenant_type}</span>
              </div>
            )}
            {connectionStatus.details.api_version && (
              <div className="detail-item">
                <span className="detail-label">API Version:</span>
                <span className="detail-value">{connectionStatus.details.api_version}</span>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="connection-help">
        <h4>Connection Help</h4>
        <div className="help-content">
          <p>If you're experiencing connection issues:</p>
          <ul>
            <li>Ensure your Xero API credentials are correctly configured</li>
            <li>Check that your Xero organization has the necessary permissions</li>
            <li>Verify that the Xero API is accessible from your network</li>
            <li>Contact your system administrator if problems persist</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default XeroConnectionStatus;
